import type { VaultItem } from '../../library/vault/vaultData'
import { MEDIA_MAX, PRICE_MIN, type When } from '../home/homeData'
import { textToHtml } from '../ofText'

/**
 * OnlyFans' "New message" (/my/chats/send, the vault's "Add to a new message"):
 * one `POST messages/queue` to lists, minus excluded lists, plus picked fans.
 * Bodies captured live on 2026-10-09 (the_avyluscious; test messages deleted).
 */

export type MessageMedia = Pick<VaultItem, 'id' | 'type'> & Partial<VaultItem>

export interface MessageDraft {
  text: string
  media: MessageMedia[]
  /** USD; 0 = free. */
  price: number
  /** "Send to": list ids ("fans", "following", or a custom list's number). */
  lists: string[]
  /** "Exclude": list ids. */
  excluded: string[]
  /** "Users": fan ids. */
  users: number[]
  when: When
}

export const EMPTY_MESSAGE: MessageDraft = { text: '', media: [], price: 0, lists: [], excluded: [], users: [], when: { kind: 'now' } }

export function recipientsOf(d: MessageDraft): { lists: number; users: number; any: boolean } {
  return { lists: d.lists.length, users: d.users.length, any: d.lists.length + d.users.length > 0 }
}

export type MessageError = 'noRecipients' | 'empty' | 'mediaMax' | 'priceMin' | 'priceNeedsMedia' | 'pastDate'

/** Why OnlyFans would refuse this message (checked before sending), or null. */
export function messageError(d: MessageDraft, now = new Date()): MessageError | null {
  if (!recipientsOf(d).any) return 'noRecipients'
  if (!d.text.trim() && d.media.length === 0) return 'empty'
  if (d.media.length > MEDIA_MAX) return 'mediaMax'
  if (d.price > 0 && d.price < PRICE_MIN) return 'priceMin'
  if (d.price > 0 && d.media.length === 0) return 'priceNeedsMedia'
  if (d.when.kind === 'schedule' && d.when.at.getTime() <= now.getTime()) return 'pastDate'
  return null
}

/**
 * `POST messages/queue` body, keys in the order the OnlyFans page sends them.
 * Videos go as {id, thumbId: 1}; the price is in dollars; excludedLists only
 * when something is excluded; Save for later is isPublishedWithPeriod alone
 * (posts also send isScheduled).
 */
export function buildMessageBody(d: MessageDraft): Record<string, unknown> {
  const body: Record<string, unknown> = {
    text: d.text.trim() ? textToHtml(d.text.replace(/\s+$/, '')) : '',
    lockedText: false,
    mediaFiles: d.media.map((m) => (m.type === 'video' ? { id: m.id, thumbId: 1 } : m.id)),
    price: d.price > 0 ? Math.round(d.price * 100) / 100 : 0,
    previews: [],
    rfTag: [],
    rfGuest: [],
    rfPartner: [],
    isForward: false,
  }
  if (d.when.kind === 'schedule') {
    body.isScheduled = 1
    body.scheduledDate = d.when.at.toISOString()
  } else if (d.when.kind === 'later') {
    body.isPublishedWithPeriod = 1
  }
  body.filters = {}
  if (d.excluded.length) body.excludedLists = [...d.excluded]
  body.userLists = [...d.lists]
  if (d.users.length) body.userIds = [...d.users]
  return body
}
